import { useRef, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import useContactSupportForm from 'hooks/useContactSupportForm';
import useFileAttachment, {
  UPLOAD_SLOW_NOTICE_MS,
  type UploadOutcome,
} from 'hooks/useFileAttachment';
import { useAppSelector } from 'store/hooks';
import {
  groupAccountSelector,
  tokenVerificationSelector,
  userActiveGroupSelector,
} from 'store/auth/auth.selector';
import { useSendContactMailMutation } from '@/services/notificationsApi';
import { cn } from '@/lib';
import {
  Button,
  DialogFooter,
  Documents,
  DropSelect,
  FormSection,
  Input,
  Separator,
  toast,
} from '@zawarski/eti-ui';
import { X } from 'lucide-react';
import { Controller, type SubmitHandler } from 'react-hook-form';
import { contactSubjectOptions } from 'constants/index';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type UploadFailureReason = Extract<UploadOutcome, { status: 'failed' }>['reason'];

// A total map rather than a ternary chain: a reason added to UploadOutcome later
// fails the build here instead of quietly falling through to a generic message.
const UPLOAD_FAILURE_MESSAGE: Record<UploadFailureReason, string> = {
  rejected: 'Could not upload the attachments. Your message was not sent -- please try again.',
  timeout: 'Uploading the attachments timed out. Your message was not sent -- please try again.',
  'in-progress':
    'The previous upload is still finishing. Your message was not sent -- please wait a moment, then try again.',
};

const ContactSupport: FC<Props> = ({ open, onOpenChange }) => {
  const userAccount = useAppSelector(tokenVerificationSelector);
  const groupAccount = useAppSelector(groupAccountSelector);
  const firstName =
    userAccount && userAccount?.pvPersonGivenName ? userAccount.pvPersonGivenName : undefined;
  const lastName =
    userAccount && userAccount?.pvPersonSurName ? userAccount.pvPersonSurName : undefined;
  const fullName =
    firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || undefined;
  const email = userAccount?.email;
  // The Palmetto file store authorizes uploads with the portal access token,
  // which is a different value from the Cognito token this app sends to its own
  // API. Accounts without one simply get no attachment area.
  const fileStoreToken = userAccount?.accessToken;
  const fileStoreUserId = userAccount?.id;
  const activeGroupId = useAppSelector(userActiveGroupSelector);
  const form = useContactSupportForm(
    fullName && email
      ? {
          name: fullName,
          email: email,
          subject: '',
          message: '',
        }
      : undefined
  );

  // `open` drives the group id: a fresh one per open, so the requirement does not
  // rest on SidebarLayout happening to unmount this modal on close.
  const { fileGroupId, setFilesQueued, setFilesDeleted, handleOnSave, triggerUpload } =
    useFileAttachment(open);

  // *: RTK Hooks
  const [sendContactMail] = useSendContactMailMutation();

  // Covers the upload and the send together, so nothing dismisses or re-submits
  // the modal mid-operation. The ref is what a double click actually races
  // against -- state has not re-rendered yet when the second click lands.
  const inFlightRef = useRef(false);
  const [inFlight, setInFlight] = useState(false);
  const [uploadSlow, setUploadSlow] = useState(false);

  //  form.handleSubmit(onSend)
  //       │
  //       ├─1─ upload queued files ──▶ Palmetto file store, grouped by fileGroupId
  //       │        │
  //       │        ├── failed / timed out ──▶ toast error, RETURN
  //       │        │                          (modal stays open, form values kept)
  //       │        └── ok ↓
  //       │
  //       ├─2─ any file in the group?
  //       │        ├── no  ──▶ payload without fileUuid (unchanged on the wire)
  //       │        └── yes ──▶ payload with fileUuid
  //       │
  //       └─3─ sendContactMail(payload)
  //                ├── ok     ──▶ success toast, close
  //                └── failed ──▶ error toast, modal stays open. The group id is
  //                               deliberately kept so a retry re-links the
  //                               already-uploaded files instead of re-uploading.
  const onSend: SubmitHandler<ContactSupportForm> = async (data) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setInFlight(true);

    const slowNoticeTimer = setTimeout(() => setUploadSlow(true), UPLOAD_SLOW_NOTICE_MS);
    const clearSlowNotice = () => {
      clearTimeout(slowNoticeTimer);
      setUploadSlow(false);
    };

    try {
      const upload = await triggerUpload();
      clearSlowNotice();
      if (upload.status === 'failed') {
        console.error('Contact support attachments could not be uploaded', {
          reason: upload.reason,
          fileGroupId,
        });
        toast.error(UPLOAD_FAILURE_MESSAGE[upload.reason]);
        return;
      }

      const payload: ContactSupportPayload = {
        name: data.name,
        email: data.email,
        subject: data.subject,
        message: data.message,
        group: groupAccount?.n || 'N/A',
        // Only when a file actually reached the store -- a message with no
        // attachments posts the same five fields it always has.
        ...(upload.fileCount > 0 ? { fileUuid: fileGroupId } : {}),
      };
      await sendContactMail(payload).unwrap();
      toast.success('Message sent successfully.');
      onOpenChange(false);
    } catch (error) {
      console.error('Contact support message failed to send', {
        group: groupAccount?.n || 'N/A',
        subject: data.subject,
        error,
      });
      toast.error('Failed to send the message. Please try again later.');
    } finally {
      clearSlowNotice();
      inFlightRef.current = false;
      setInFlight(false);
    }
  };

  if (!open) return null;

  // Rendered into document.body so the modal escapes any ancestor stacking
  // context (e.g. the z-20 FormMaxLayout wrapper) and never paints under the sidebar.
  return createPortal(
    <>
      {/* Backdrop with blur */}
      <div
        className='ci:fixed ci:inset-0 ci:z-50 ci:bg-black ci:opacity-50 ci:backdrop-blur-sm'
        onClick={!inFlight ? () => onOpenChange(false) : undefined}
      />
      {/* Modal Content */}
      <div className='ci:fixed ci:inset-0 ci:z-50 ci:flex ci:items-center ci:justify-center ci:p-4'>
        <div
          className={cn(
            'ci:w-full ci:md:max-w-[min(40vw,920px)]',
            'ci:max-h-[95vh] ci:h-auto',
            'ci:border ci:border-stroke-subtle ci:p-0 ci:pt-4',
            'ci:overflow-hidden ci:flex ci:flex-col',
            'ci:rounded-lg ci:bg-background ci:shadow-xl'
          )}
          onClick={(e) => e.stopPropagation()}>
          {/* HEADER */}
          <div className={'ci:pl-4 ci:md:pr-6 ci:shrink-0'}>
            <div className={'ci:flex ci:items-start ci:justify-between'}>
              <div className={'ci:flex ci:flex-col ci:gap-2 ci:text-foreground'}>
                <p className={'ci:font-medium ci:text-lg'}>Contact support</p>
                <p className={'ci:text-sm ci:md:pr-6 ci:mb-4'}>
                  A response will be provided within 2 working days.
                </p>
              </div>
              {/* Close Button */}
              <button
                onClick={() => onOpenChange(false)}
                disabled={inFlight}
                className='ci:p-2 ci:rounded-full ci:cursor-pointer ci:text-foreground ci:hover:bg-(--foreground)/10 ci:transition-colors ci:disabled:cursor-not-allowed ci:disabled:opacity-50'
                aria-label='Close modal'>
                <X className='ci:h-4 ci:w-4' />
              </button>
            </div>
          </div>
          <Separator />
          <div
            className={
              'ci:flex ci:flex-col ci:gap-4 ci:p-4 ci:overflow-y-auto ci:max-h-[620px] ci:bg-muted'
            }>
            <FormSection title={'Email Details'} uppercase={true}>
              <div className={'ci:flex ci:flex-col ci:w-full ci:h-fit ci:gap-4 ci:p-4'}>
                <Controller
                  control={form.control}
                  name={'subject'}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <DropSelect
                      required
                      options={contactSubjectOptions}
                      label='Subject'
                      placeholder='Please select an option'
                      onChange={onChange}
                      value={value}
                      error={!!error}
                      helperText={error && error.message ? error.message : undefined}
                    />
                  )}
                />
                {/* Paired on one row. Split at lg rather than the md used
                    elsewhere: this modal is capped at min(40vw,920px), so at a
                    768px viewport it is ~307px wide and two columns would leave
                    each field too narrow to read an address in. */}
                <div className={'ci:grid ci:grid-cols-1 ci:lg:grid-cols-2 ci:gap-4'}>
                  <Controller
                    control={form.control}
                    name={'name'}
                    render={({ field: { value } }) => (
                      <Input
                        disabled={true}
                        className='ci:text-foreground'
                        label='Name'
                        placeholder={'Please enter name'}
                        value={value}
                      />
                    )}
                  />
                  <Controller
                    control={form.control}
                    name={'email'}
                    render={({ field: { value } }) => (
                      <Input
                        disabled={true}
                        className='ci:text-foreground'
                        label='Email'
                        placeholder={'Please enter email'}
                        value={value}
                      />
                    )}
                  />
                </div>
                <Controller
                  control={form.control}
                  name='message'
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      required
                      multiple
                      rows={5}
                      className='ci:text-foreground ci:min-h-[120px]'
                      label='Details'
                      placeholder={'Add the message you want to send'}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error && error.message ? error.message : undefined}
                    />
                  )}
                />
              </div>
            </FormSection>
            {/* Attachments are absent, not disabled, when the account carries no
                file-store credential -- the modal then behaves as it always has.
                readOnly stays pinned false: a truthy value swaps the drop zone
                for a read-only list and silently discards whatever was queued. */}
            {fileStoreToken && fileStoreUserId ? (
              // inert while sending, rather than unmounted: the upload runs
              // inside this component, so removing it would cut the operation
              // it is reporting on. What has to stop is input. The library's
              // "Add File" button lives in the section header, outside the area
              // its progress bar replaces, so it stays clickable for the whole
              // upload -- and a file queued at that point is appended to a list
              // the running upload has already read past, then cleared when the
              // upload completes. The file would be accepted on screen, counted
              // by nothing, and gone by the time the success toast appears.
              // inert blocks pointer and keyboard alike, unlike pointer-events.
              <div inert={inFlight}>
                <Documents
                  uppercase
                  readOnly={false}
                  uuid={fileGroupId}
                  userID={fileStoreUserId}
                  authToken={fileStoreToken}
                  // Required by the prop type and read by nothing in eti-ui
                  // 3.7.2 -- the outer component forwards six props to its inner
                  // drop zone and this is not among them. The real selector value
                  // is passed anyway rather than a placeholder, so the day a
                  // version starts reading it, this keeps working.
                  pvDataID={activeGroupId}
                  filesQueuedCallback={setFilesQueued}
                  filesDeletedCallback={setFilesDeleted}
                  onSave={handleOnSave}
                />
              </div>
            ) : null}
            {/* The live region stays mounted and goes screen-reader-only when
                idle, rather than mounting on demand. Most screen readers
                announce text that changes inside a region they were already
                watching and stay silent when a region appears already
                populated -- and this notice is the only durable in-flight
                signal, so the users who need it most are the ones who would
                miss it. sr-only keeps it in the accessibility tree while
                taking it out of the flex flow, so no empty gap is left behind. */}
            <p
              role='status'
              className={
                uploadSlow
                  ? 'ci:rounded ci:border ci:border-stroke-subtle ci:bg-card ci:p-3 ci:text-sm ci:text-foreground'
                  : 'ci:sr-only'
              }>
              {uploadSlow ? 'Still uploading -- your message has not been sent yet.' : ''}
            </p>
          </div>
          <Separator />
          <DialogFooter className={'ci:p-4 ci:bg-card'}>
            <div className={'ci:flex ci:w-full ci:flex-row-reverse ci:gap-2'}>
              <Button
                loading={inFlight}
                disabled={inFlight}
                onClick={!inFlight ? form.handleSubmit(onSend) : undefined}>
                Send
              </Button>
              <Button disabled={inFlight} variant='secondary' onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </div>
          </DialogFooter>
        </div>
      </div>
    </>,
    document.body
  );
};
export default ContactSupport;
